/**
 * Shared grid constants for the planner module.
 * Centralizes all dimension and configuration values used across
 * PlanView, PlanBlock, GridCell, useBlockPositions, and other components.
 */

// =============================================================================
// GRID DIMENSIONS
// =============================================================================

/** Width of each week column in the grid (pixels) */
export const CELL_WIDTH = 136;

/** Height of each row in the grid (pixels) */
export const ROW_HEIGHT = 56;

/** Height of each cell (same as ROW_HEIGHT for square-ish cells) */
export const CELL_HEIGHT = ROW_HEIGHT;

/** Margin inside blocks to prevent edge-to-edge rendering (pixels) */
export const BLOCK_MARGIN = 4;

// =============================================================================
// SCROLL CONFIGURATION
// =============================================================================

/** Multiplier for wheel scroll to make it feel smoother */
export const REDUCED_SCROLL_MULTIPLIER = 0.35;

/** Number of weeks from edge before triggering infinite scroll load */
export const SCROLL_THRESHOLD_WEEKS = 5;

/** Number of weeks to render beyond viewport on each side for smooth scrolling */
export const WEEK_WINDOW_BUFFER = 6;

/** Pixel threshold for infinite scroll (CELL_WIDTH * SCROLL_THRESHOLD_WEEKS) */
export const SCROLL_THRESHOLD_PX = CELL_WIDTH * SCROLL_THRESHOLD_WEEKS;

// =============================================================================
// REVIEW VIEW CONSTANTS
// =============================================================================

/** Width of the week column in review view (pixels) */
export const WEEK_COLUMN_WIDTH = 210;

/** Width of the notes column in review view (pixels) */
export const NOTES_COLUMN_WIDTH = 300;

/** Height of the sticky table header in review view (pixels) */
export const TABLE_HEADER_HEIGHT = 41;

/** Delay for year badge transition animation (ms) */
export const YEAR_TRANSITION_DELAY = 500;

// =============================================================================
// PREVIEW TABLE CONSTANTS
// =============================================================================

/** Number of rows to show in preview table (Plan view) */
export const PREVIEW_ROWS = 8;

/** Number of rows to show in preview table (Review view) */
export const PREVIEW_REVIEW_ROWS = 10;

// =============================================================================
// DERIVED CALCULATIONS
// =============================================================================

/**
 * Calculate pixel position from grid column index
 * @param {number} colIndex - Zero-based column index
 * @returns {number} X position in pixels
 */
export const colToPixelX = (colIndex) => colIndex * CELL_WIDTH + BLOCK_MARGIN;

/**
 * Calculate pixel position from grid row number
 * @param {number} rowNum - One-based row number
 * @returns {number} Y position in pixels
 */
export const rowToPixelY = (rowNum) => (rowNum - 1) * ROW_HEIGHT + BLOCK_MARGIN;

/**
 * Calculate block width from week span
 * @param {number} weekSpan - Number of weeks the block spans
 * @returns {number} Width in pixels
 */
export const weekSpanToWidth = (weekSpan) => weekSpan * CELL_WIDTH - BLOCK_MARGIN * 2;

/**
 * Calculate block height (fixed, based on cell height minus margins)
 * @returns {number} Height in pixels
 */
export const getBlockHeight = () => CELL_HEIGHT - BLOCK_MARGIN * 2;

/**
 * Calculate grid column index from pixel X position
 * @param {number} pixelX - X position in pixels
 * @returns {number} Zero-based column index
 */
export const pixelXToCol = (pixelX) => Math.round((pixelX - BLOCK_MARGIN) / CELL_WIDTH);

/**
 * Calculate grid row number from pixel Y position
 * @param {number} pixelY - Y position in pixels
 * @returns {number} One-based row number
 */
export const pixelYToRow = (pixelY) => Math.round((pixelY - BLOCK_MARGIN) / ROW_HEIGHT) + 1;

/**
 * Snap a pixel X value to the nearest grid column boundary
 * @param {number} pixelX - X position in pixels
 * @returns {number} Snapped X position in pixels
 */
export const snapToGridX = (pixelX) => colToPixelX(pixelXToCol(pixelX));

/**
 * Snap a pixel Y value to the nearest grid row boundary
 * @param {number} pixelY - Y position in pixels
 * @returns {number} Snapped Y position in pixels
 */
export const snapToGridY = (pixelY) => rowToPixelY(pixelYToRow(pixelY));
